import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { useAuth } from '@frontegg/react';
import { useTenant } from 'src/hooks/misc/useTenant';
import { Logo } from '@insight-health-ai/roseate/components';
import { Tooltip } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { analytics } from 'src/services';

const Toggler = styled.div`
  position: absolute;
  right: 0;
  bottom: -8px;
  transform: translateX(50%);
  height: 1.75rem;
  width: 1.75rem;
  border-radius: 50%;
  background-color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.3s ease-in-out;
`;

const SideNavbarLogo = ({ isMinimized, setIsMinimized, setIsExpanding }) => {
  const { currentPractitionerEntitlements } = useSelector(
    (state) => state?.practitionerState
  );
  const navigate = useNavigate();
  const { user } = useAuth();
  const tenant = useTenant();

  const UIBaseUrl = import.meta.env.VITE_AMBIENT_UI_BASE_URL;
  const isAuraPro = currentPractitionerEntitlements?.plans?.some(
    (plan) => plan?.metadata?.type === 'pro'
  );
  const isPLGUser = currentPractitionerEntitlements?.plans?.some(
    (plan) => plan?.metadata?.type === 'plg'
  );
  const isAuraProTrial = currentPractitionerEntitlements?.plans?.some(
    (plan) => plan?.metadata?.trial === true
  );
  const isAuraTeams = currentPractitionerEntitlements?.plans?.some(
    (plan) => plan?.metadata?.type === 'teams'
  );
  const isAuraEnterprise = currentPractitionerEntitlements?.plans?.some(
    (plan) => plan?.metadata?.type === 'enterprise'
  );
  const auraProTrialPlanExpiry = currentPractitionerEntitlements?.plan_expiry;
  const auraProTrialRemainingDays = auraProTrialPlanExpiry
    ? dayjs(auraProTrialPlanExpiry).diff(dayjs(), 'day')
    : null;
  const auraProTrialRemainingSeconds = auraProTrialPlanExpiry
    ? dayjs(auraProTrialPlanExpiry).diff(dayjs(), 'second')
    : null;
  const isAuraProTrialExpired =
    isAuraProTrial && auraProTrialRemainingSeconds <= 0;

  const [timeRemaining, setTimeRemaining] = useState(null);

  useEffect(() => {
    if (auraProTrialRemainingDays !== null) {
      const interval = setInterval(() => {
        const diffMs = dayjs(auraProTrialPlanExpiry).diff(dayjs());

        if (diffMs <= 0) {
          clearInterval(interval);
          setTimeRemaining('00:00:00:00');
        } else {
          const totalSeconds = Math.floor(diffMs / 1000);
          const days = Math.floor(totalSeconds / 86400);
          const hours = Math.floor((totalSeconds % 86400) / 3600);
          const minutes = Math.floor((totalSeconds % 3600) / 60);

          setTimeRemaining(
            `${String(days).padStart(2, '0')}d ${String(hours).padStart(
              2,
              '0'
            )}h ${String(minutes).padStart(2, '0')}m`
          );
        }
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [auraProTrialPlanExpiry, auraProTrialRemainingDays]);

  const togglerJsx = (
    <Tooltip
      title={`${isMinimized ? 'Expand' : 'Collapse'} sidebar`}
      placement="rightBottom"
    >
      <Toggler
        className="sidenav-toggler h-7 w-7 shadow-md z-[101] hover:bg-gray-100"
        onClick={() => {
          analytics.track(
            `Clicked ${isMinimized ? 'Expand' : 'Collapse'} Sidebar`,
            {
              email: user?.email,
            }
          );
          const newState = !isMinimized;
          localStorage.setItem('minimizedSideNavBarPreference', newState);
          setIsMinimized(newState);
          setIsExpanding(true);
          setTimeout(() => {
            setIsExpanding(false);
          }, 400);
        }}
      >
        {isMinimized ? <RightOutlined /> : <LeftOutlined />}
      </Toggler>
    </Tooltip>
  );

  if (isMinimized) {
    const getLogo = () => {
      if (tenant === 'OP') {
        const src = 'https://images.insighthealth.ai/office-practicum/logo.svg';
        return (
          <img
            src={src}
            width="auto"
            height="auto"
            className="w-full cursor-pointer"
            alt="Insight Logo"
          />
        );
      }

      if (tenant === 'OpenDr') {
        const src = 'https://images.insighthealth.ai/open-doctor/logo.png';
        return (
          <img
            src={src}
            width="auto"
            height="auto"
            className="w-full cursor-pointer mt-4"
            alt="OpenDr Logo"
          />
        );
      }

      return <Logo compact />;
    };

    return (
      <div className="p-4 flex items-center justify-center relative">
        {getLogo()}
        {togglerJsx}
      </div>
    );
  }

  const getLogo = () => {
    if (tenant === 'OP') {
      return (
        <div className="flex flex-col justify-center items-center max-w-[120px]">
          <img
            src="https://images.insighthealth.ai/office-practicum/logo.svg"
            alt="Office Practicum Logo"
            className="w-full"
          />
          <img
            src="https://images.insighthealth.ai/insight-health-attribution.svg"
            alt="Powered by Insight Health"
            className="w-[87%]"
            onClick={() => {
              window.open('https://www.insighthealth.ai', '_blank');
            }}
          />
        </div>
      );
    }
    if (tenant === 'OpenDr') {
      const src = 'https://images.insighthealth.ai/open-doctor/logo.png';
      return (
        <img
          src={src}
          width="auto"
          height="auto"
          className="w-full cursor-pointer mt-4"
          alt="OpenDr Logo"
        />
      );
    }
    return <Logo />;
  };

  return (
    <div className="p-4 pb-3 sm:p-5 sm:pb-4 relative">
      <div
        className="logo flex flex-col items-center side-navbar_logo justify-center cursor-pointer"
        onClick={() => navigate('/')}
      >
        {getLogo()}
      </div>
      <div className="empty:hidden">
        {UIBaseUrl === 'http://app.lumi.build' ? (
          <div className="mt-1 text-[#fb6542] font-xl flex justify-center ml-10 font-mono ">
            STAGING
          </div>
        ) : (
          ''
        )}
        {isAuraProTrial && !isAuraProTrialExpired ? (
          <div className="flex items-center mt-4 ml-[70px]">
            <div className="rounded-xl bg-[#555555] text-white font-xl flex justify-end items-center font-mono w-[100px] px-2 py-1">
              <span className="mr-1">PRO</span>
              <span className="bg-[#fb6542] text-white font-sans text-[10px] font-bold px-1 rounded mr-2">
                TRIAL
              </span>
            </div>
          </div>
        ) : (
          isAuraPro &&
          !isAuraTeams &&
          !isAuraProTrial && (
            <div className="rounded-xl mt-4 bg-[#555555] text-white font-xl flex justify-center ml-[90px] font-mono w-[75px]">
              PRO
            </div>
          )
        )}
        {(!isAuraPro || (isAuraProTrial && isAuraProTrialExpired)) &&
          isPLGUser && (
            <div className="rounded-xl mt-4 bg-[#f0f0f0] text-black font-xl flex justify-center ml-[90px] font-mono w-[75px] ">
              FREE
            </div>
          )}
        {isAuraTeams && (
          <div className="rounded-xl mt-4 bg-[#555555] text-white font-xl flex justify-center ml-[90px] font-mono w-[75px]">
            TEAMS
          </div>
        )}
        {isAuraEnterprise && (
          <div className="rounded-xl mt-4 bg-[#555555] text-white font-xl flex justify-center ml-[80px] font-mono w-[110px]">
            ENTERPRISE
          </div>
        )}
        {isAuraProTrial && auraProTrialRemainingDays !== null && (
          <div className="mt-4 flex items-center">
            <div className="text-gray-500 text-xs mr-2 flex-grow flex items-center">
              {auraProTrialRemainingSeconds <= 0
                ? 'Free trial of Aura Pro has expired.'
                : `Free trial ends in ${timeRemaining}`}
            </div>
          </div>
        )}
      </div>
      {togglerJsx}
    </div>
  );
};

export default SideNavbarLogo;
